import React, { useRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { cva } from "class-variance-authority";
import { cn } from "../../utils/classNames";

const buttonVariants = cva("sp-button", {
  variants: {
    variant: {
      default: "sp-button--default",
      destructive: "sp-button--destructive",
      outline: "sp-button--outline",
      secondary: "sp-button--secondary",
      wireframe: "sp-button--wireframe",
      ghost: "sp-button--ghost",
      link: "sp-button--link",
      success: "sp-button--success",
      warning: "sp-button--warning",
      space: "sp-button--space",
      filter: "sp-button--filter",
      "filter-active": "sp-button--filter-active",
      "icon-dark": "sp-button--icon-dark",
    },
    size: {
      default: "",
      sm: "sp-button--sm",
      lg: "sp-button--lg",
      xl: "sp-button--xl",
      icon: "sp-button--icon",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

const Button = React.forwardRef(
  ({
    className,
    variant,
    size,
    asChild = false,
    disabled = false,
    children,
    ...props
  }, ref) => {
    const buttonRef = useRef(null);

    useImperativeHandle(ref, () => buttonRef.current);

    if (asChild && React.isValidElement(children)) {
      // For asChild, we merge classes and forward properties
      const childClass = children.props.className || "";
      const mergedClass = cn(buttonVariants({ variant, size, className }), childClass);

      return React.cloneElement(children, {
        className: mergedClass,
        ...props,
      });
    }

    return (
      <button
        ref={buttonRef}
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

Button.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    "default",
    "destructive",
    "outline",
    "secondary",
    "ghost",
    "link",
    "success",
    "warning",
    "space",
    "wireframe",
    "filter",
    "filter-active",
    "icon-dark"
  ]),
  size: PropTypes.oneOf(["default", "sm", "lg", "xl", "icon"]),
  asChild: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export { Button, buttonVariants };
export default Button;
